// src/components/chat/ChatSidebar.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ С ВЕРТИКАЛЬНЫМИ ВКЛАДКАМИ
import React, { useState, useEffect, useMemo } from 'react';
import { ListGroup, Badge, Form, InputGroup, Button, Spinner, Nav } from 'react-bootstrap';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI } from '../../services/api';
import ChatCreateGroupModal from './ChatCreateGroupModal';

const ChatSidebar = ({ onSelectRoom }) => {
  const {
    rooms,
    currentRoom,
    loadingRooms,
    unreadCounts,
    getOrCreatePrivateRoom,
  } = useChat();
  const { user } = useAuth();
  
  // Состояние для активной вкладки
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Кэш для имен пользователей
  const [userNames, setUserNames] = useState({});
  const [loadingUsers, setLoadingUsers] = useState({});

  // Определение вкладок
  const tabs = [
    { key: 'all', icon: 'bi-chat-dots', label: 'Все чаты' },
    { key: 'private', icon: 'bi-person', label: 'Личные' },
    { key: 'group', icon: 'bi-people', label: 'Группы' },
    { key: 'task', icon: 'bi-list-task', label: 'Задачи' }
  ];

  // Подсчет количества комнат по типам
  const roomCounts = useMemo(() => {
    const counts = {
      all: rooms.length,
      private: rooms.filter(r => r.type === 'private').length,
      group: rooms.filter(r => r.type === 'group').length,
      task: rooms.filter(r => r.type === 'task').length
    };
    return counts;
  }, [rooms]);

  // Подсчет непрочитанных сообщений по типам
  const unreadCountsByType = useMemo(() => {
    const counts = { all: 0, private: 0, group: 0, task: 0 };
    
    rooms.forEach(room => {
      const unread = unreadCounts[room.id] || 0;
      counts.all += unread;
      
      if (room.type === 'private') {
        counts.private += unread;
      } else if (room.type === 'group') {
        counts.group += unread;
      } else if (room.type === 'task') {
        counts.task += unread;
      }
    });
    
    return counts;
  }, [rooms, unreadCounts]);

  // Загрузка имени пользователя по ID
  const loadUserName = async (userId) => {
    if (!userId || userNames[userId] || loadingUsers[userId]) return;
    
    setLoadingUsers(prev => ({ ...prev, [userId]: true }));
    try {
      const userData = await usersAPI.getUser(userId);
      const displayName = getDisplayName(userData);
      setUserNames(prev => ({ ...prev, [userId]: displayName }));
    } catch (err) {
      console.error(`Error loading user ${userId}:`, err);
      setUserNames(prev => ({ ...prev, [userId]: `Пользователь #${userId}` }));
    } finally {
      setLoadingUsers(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Получение отображаемого имени пользователя
  const getDisplayName = (userData) => {
    if (!userData) return 'Пользователь';
    
    // Проверяем Telegram данные
    if (userData.telegram_first_name && userData.telegram_last_name) {
      return `${userData.telegram_first_name} ${userData.telegram_last_name}`;
    }
    if (userData.telegram_first_name) {
      return userData.telegram_first_name;
    }
    if (userData.telegram_last_name) {
      return userData.telegram_last_name;
    }
    
    // Проверяем обычное имя
    if (userData.name && userData.name !== 'Пользователь') {
      return userData.name;
    }
    
    // Проверяем username
    if (userData.telegram_username) {
      return `@${userData.telegram_username}`;
    }
    
    return `Пользователь #${userData.id}`;
  };

  // Загружаем имена для всех участников комнат
  useEffect(() => {
    if (!rooms.length || !user) return;

    rooms.forEach(room => {
      if (room.type === 'private') {
        // Для личных диалогов загружаем собеседника
        if (room.participants && Array.isArray(room.participants)) {
          room.participants.forEach(p => {
            if (p.user_id !== user.id) {
              loadUserName(p.user_id);
            }
          });
        }
      }
    });
  }, [rooms, user]);

  // Стили для правильной flex-верстки
  const sidebarStyles = `
    .chat-sidebar {
      height: 100%;
      display: flex;
      flex-direction: row;
      background-color: #fff;
    }
    
    .chat-sidebar-tabs {
      width: 60px;
      background-color: #f8f9fa;
      border-right: 1px solid #dee2e6;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 0;
      gap: 8px;
    }
    
    .chat-sidebar-tabs .nav-link {
      width: 40px;
      height: 40px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
      color: #6c757d;
      transition: all 0.2s ease;
      position: relative;
    }
    
    .chat-sidebar-tabs .nav-link:hover {
      background-color: rgba(141, 192, 251, 0.1);
      color: #8dc0fb;
    }
    
    .chat-sidebar-tabs .nav-link.active {
      background: linear-gradient(135deg, #8dc0fb 0%, #6ba0d9 100%);
      color: white;
    }
    
    .chat-sidebar-tabs .nav-link i {
      font-size: 1.2rem;
    }
    
    .tab-badge {
      position: absolute;
      top: 2px;
      right: 2px;
      font-size: 0.6rem;
      padding: 2px 4px;
      min-width: 16px;
      height: 16px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
    }
    
    .chat-sidebar-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;
      background-color: #fff;
    }
    
    .chat-sidebar .sidebar-header {
      flex-shrink: 0;
      background-color: #fff;
      border-bottom: 1px solid #dee2e6;
    }
    
    .chat-sidebar .rooms-list {
      flex: 1 1 auto;
      overflow-y: auto;
      min-height: 0;
      scrollbar-width: thin;
    }
    
    .chat-sidebar .rooms-list::-webkit-scrollbar {
      width: 6px;
    }
    
    .chat-sidebar .rooms-list::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .chat-sidebar .rooms-list::-webkit-scrollbar-thumb {
      background-color: #8dc0fb;
      border-radius: 3px;
    }
    
    .chat-sidebar .rooms-list::-webkit-scrollbar-thumb:hover {
      background-color: #6ba0d9;
    }
    
    .chat-sidebar .sidebar-footer {
      flex-shrink: 0;
      background-color: #f8f9fa;
      border-top: 1px solid #dee2e6;
    }
    
    .chat-sidebar .search-results {
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 1000;
      background: white;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      max-height: 300px;
      overflow-y: auto;
    }
    
    .chat-sidebar .list-group-item {
      border-left: none;
      border-right: none;
      transition: all 0.2s ease;
    }
    
    .chat-sidebar .list-group-item:first-child {
      border-top: none;
    }
    
    .chat-sidebar .list-group-item:last-child {
      border-bottom: none;
    }
    
    .chat-sidebar .list-group-item:hover {
      background-color: rgba(141, 192, 251, 0.1);
    }
    
    .chat-sidebar .list-group-item.active {
      background: linear-gradient(135deg, #8dc0fb 0%, #6ba0d9 100%);
      border-color: transparent;
      color: white;
    }
    
    .chat-sidebar .list-group-item.active .text-muted {
      color: rgba(255,255,255,0.8) !important;
    }
    
    .chat-sidebar .list-group-item.active small {
      color: rgba(255,255,255,0.7) !important;
    }
    
    @media (max-width: 768px) {
      .chat-sidebar-tabs {
        width: 50px;
      }
      
      .chat-sidebar-tabs .nav-link {
        width: 36px;
        height: 36px;
      }
    }
  `;

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин`;
    if (diffHours < 24) return `${diffHours} ч`;
    if (diffDays < 7) return `${diffDays} дн`;
    return date.toLocaleDateString('ru-RU');
  };

  const getRoomIcon = (room) => {
    if (room.type === 'group') return 'bi-people';
    if (room.type === 'task') return 'bi-list-task';
    return 'bi-person';
  };

  // Функция для обрезки текста
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Функция получения имени комнаты
  const getRoomName = (room) => {
    // Для групповых чатов
    if (room.type === 'group') {
      return room.name || 'Группа';
    }
    
    // Для чатов, привязанных к задачам
    if (room.type === 'task') {
      if (room.task_title) {
        return truncateText(room.task_title, 30);
      }
      return `Задача #${room.task_id || '?'}`;
    }
    
    // Для личных диалогов (private)
    if (room.type === 'private' && user) {
      if (room.participants && Array.isArray(room.participants)) {
        const otherParticipant = room.participants.find(p => p.user_id !== user.id);
        
        if (otherParticipant) {
          if (userNames[otherParticipant.user_id]) {
            return truncateText(userNames[otherParticipant.user_id], 25);
          }
          
          if (otherParticipant.user_name && otherParticipant.user_name !== 'Пользователь') {
            return truncateText(otherParticipant.user_name, 25);
          }
          
          if (loadingUsers[otherParticipant.user_id]) {
            return 'Загрузка...';
          }
          
          loadUserName(otherParticipant.user_id);
        }
      }
      
      if (room.other_user_name) {
        return truncateText(room.other_user_name, 25);
      }
    }
    
    return 'Диалог';
  };

  // Функция для получения полной информации о комнате (для tooltip)
  const getRoomFullInfo = (room) => {
    if (room.type === 'private' && user && room.participants) {
      const otherParticipant = room.participants.find(p => p.user_id !== user.id);
      if (otherParticipant) {
        if (userNames[otherParticipant.user_id]) {
          return userNames[otherParticipant.user_id];
        }
        if (otherParticipant.user_name) {
          return otherParticipant.user_name;
        }
      }
    }
    return getRoomName(room);
  };

  const handleSearchUser = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const data = await usersAPI.getUsers({ search: searchTerm.trim(), limit: 10 });
      const usersList = data.users || data.data || data;
      setSearchResults(Array.isArray(usersList) ? usersList : []);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setSearching(false);
    }
  };

  const handleStartPrivateChat = async (otherUserId) => {
    const room = await getOrCreatePrivateRoom(otherUserId);
    if (room) {
      onSelectRoom(room);
      setSearchTerm('');
      setSearchResults([]);
    }
  };

  // Фильтрация комнат по активной вкладке и поисковому запросу
  const filteredRooms = useMemo(() => {
    let filtered = rooms;
    
    // Фильтр по типу
    if (activeTab !== 'all') {
      filtered = filtered.filter(room => room.type === activeTab);
    }
    
    // Фильтр по поиску
    if (searchTerm.trim()) {
      filtered = filtered.filter(room => {
        const name = getRoomName(room).toLowerCase();
        return name.includes(searchTerm.toLowerCase());
      });
    }
    
    return filtered;
  }, [rooms, activeTab, searchTerm]);

  return (
    <>
      <style>{sidebarStyles}</style>
      <div className="chat-sidebar">
        {/* Вертикальные вкладки */}
        <div className="chat-sidebar-tabs">
          {tabs.map(tab => (
            <Nav.Link
              key={tab.key}
              className={activeTab === tab.key ? 'active' : ''}
              onClick={() => setActiveTab(tab.key)}
              title={`${tab.label} (${roomCounts[tab.key]})`}
            >
              <i className={`bi ${tab.icon}`}></i>
              {unreadCountsByType[tab.key] > 0 && (
                <Badge 
                  bg="danger" 
                  className="tab-badge"
                >
                  {unreadCountsByType[tab.key] > 9 ? '9+' : unreadCountsByType[tab.key]}
                </Badge>
              )}
            </Nav.Link>
          ))}
        </div>

        {/* Основной контент */}
        <div className="chat-sidebar-content">
          {/* Хедер с поиском */}
          <div className="sidebar-header p-3">
            <h6 className="mb-3 d-flex align-items-center">
              <i className={`bi ${tabs.find(t => t.key === activeTab)?.icon} me-2`}></i>
              {tabs.find(t => t.key === activeTab)?.label}
              <Badge bg="secondary" className="ms-2">{filteredRooms.length}</Badge>
            </h6>
            <div className="position-relative">
              <Form onSubmit={handleSearchUser}>
                <InputGroup size="sm">
                  <Form.Control
                    placeholder="Поиск пользователей..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="border-end-0"
                    style={{ 
                      borderTopRightRadius: '0',
                      borderBottomRightRadius: '0'
                    }}
                  />
                  <Button 
                    type="submit" 
                    variant="outline-primary" 
                    disabled={searching}
                    style={{ 
                      borderTopLeftRadius: '0',
                      borderBottomLeftRadius: '0'
                    }}
                  >
                    {searching ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <i className="bi bi-search"></i>
                    )}
                  </Button>
                </InputGroup>
              </Form>
              
              {/* Результаты поиска */}
              {searchResults.length > 0 && (
                <div className="search-results mt-1">
                  <ListGroup variant="flush">
                    {searchResults.map(u => (
                      <ListGroup.Item 
                        key={u.id} 
                        action 
                        onClick={() => handleStartPrivateChat(u.id)}
                        className="d-flex align-items-center gap-2 py-2"
                      >
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                          <i className="bi bi-person text-primary"></i>
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-semibold small">{getDisplayName(u)}</div>
                          <small className="text-muted">
                            @{u.telegram_username || 'нет username'}
                          </small>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>
              )}
            </div>
          </div>

          {/* Список комнат */}
          <div className="rooms-list">
            {loadingRooms ? (
              <div className="text-center p-4">
                <Spinner animation="border" size="sm" variant="primary" />
                <div className="mt-2 small text-muted">Загрузка чатов...</div>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="text-center p-4 text-muted">
                <i className="bi bi-inbox display-4 opacity-50"></i>
                <p className="mt-2 mb-1 fw-semibold">Нет чатов</p>
                <small>
                  {activeTab === 'all' && 'Найдите пользователя через поиск, чтобы начать диалог'}
                  {activeTab === 'private' && 'У вас пока нет личных диалогов'}
                  {activeTab === 'group' && 'Вы не состоите ни в одной группе'}
                  {activeTab === 'task' && 'У вас пока нет чатов по задачам'}
                </small>
              </div>
            ) : (
              <ListGroup variant="flush">
                {filteredRooms.map(room => {
  const roomName = getRoomName(room);
  const fullRoomName = getRoomFullInfo(room);
  const unreadCount = unreadCounts[room.id] || 0;
  
  return (
    <ListGroup.Item
      key={room.id}
      action
      active={currentRoom?.id === room.id}
      onClick={() => onSelectRoom(room)}
      className="d-flex align-items-start gap-2 py-3 px-3"
      title={fullRoomName}
    >
      <div className="position-relative">
        <div className={`rounded-circle p-2 ${currentRoom?.id === room.id ? 'bg-white bg-opacity-25' : 'bg-primary bg-opacity-10'}`}>
          <i className={`bi ${getRoomIcon(room)} ${currentRoom?.id === room.id ? 'text-white' : 'text-primary'}`}></i>
        </div>
        {room.is_locked && (
          <span className="position-absolute top-0 start-100 translate-middle p-1 bg-danger border border-light rounded-circle">
            <span className="visually-hidden">Locked</span>
          </span>
        )}
        {/* Индикатор непрочитанных на иконке */}
        {unreadCount > 0 && !currentRoom?.id === room.id && (
          <span className="position-absolute top-0 start-0 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '0.6rem' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
      
      <div className="flex-grow-1 min-w-0">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 text-truncate" style={{ maxWidth: '150px' }}>
            {roomName}
            {loadingUsers[room.participants?.find(p => p.user_id !== user?.id)?.user_id] && 
              room.type === 'private' && 
              !userNames[room.participants?.find(p => p.user_id !== user?.id)?.user_id] && (
              <Spinner animation="border" size="sm" className="ms-2" />
            )}
          </h6>
          <small className={currentRoom?.id === room.id ? 'text-white-50' : 'text-muted'}>
            {formatTime(room.updated_at || room.created_at)}
          </small>
        </div>
        
        <div className="d-flex justify-content-between align-items-center mt-1">
          <p className={`small text-truncate mb-0 ${currentRoom?.id === room.id ? 'text-white-50' : 'text-muted'}`} style={{ maxWidth: '150px' }}>
            {room.last_message?.content ? truncateText(room.last_message.content, 30) : 'Нет сообщений'}
          </p>
          {unreadCount > 0 && (
            <Badge 
              bg={currentRoom?.id === room.id ? 'light' : 'danger'} 
              pill 
              className={currentRoom?.id === room.id ? 'text-dark' : ''}
            >
              {unreadCount}
            </Badge>
          )}
        </div>
        
        {/* Дополнительная информация о комнате */}
        <div className="mt-1">
          {room.type === 'group' && (
            <small className={currentRoom?.id === room.id ? 'text-white-50' : 'text-muted'}>
              <i className="bi bi-people me-1"></i>
              {room.participants?.length || 0} участников
            </small>
          )}
          
          {room.type === 'task' && room.task_id && (
            <small className={currentRoom?.id === room.id ? 'text-white-50' : 'text-muted'}>
              <i className="bi bi-hash me-1"></i>
              ID: {room.task_id}
            </small>
          )}
        </div>
      </div>
    </ListGroup.Item>
  );
})}

              </ListGroup>
            )}
          </div>

          {/* Футер с кнопкой создания группы */}
          <div className="sidebar-footer p-3">
            <Button
              variant="outline-primary"
              className="w-100 d-flex align-items-center justify-content-center gap-2"
              onClick={() => setShowCreateGroup(true)}
              size="sm"
            >
              <i className="bi bi-plus-lg"></i>
              <span>Создать группу</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Модальное окно создания группы */}
      <ChatCreateGroupModal
        show={showCreateGroup}
        onHide={() => setShowCreateGroup(false)}
        onGroupCreated={(room) => {
          setShowCreateGroup(false);
          onSelectRoom(room);
        }}
      />
    </>
  );
};

export default ChatSidebar;